<!-- Copyright (C) 2017, 2018 DecElite LLC - All Rights Reserved. May not be distributed or copied. -->
<template>
  <v-layout class="md--viewer">
    <div class = "md--content mx-3">
      <v-breadcrumbs
        divider="/"
        :items="crumbs" />
      <div
        class="md--text"
        v-html="doc" />
      <child-list :children="meta.children"/>
    </div>
    <div
      v-show="nav.length"
      class="sidebar hidden-sm-and-down">
      <div :style="scrollStyle">
        <h3 class="title grey--text text--darken-2 pb-3 pt-2">Table of Contents</h3>
        <ul>
          <li
            v-for="link in nav"
            :class = "'mdlevel' + link.level + ' ' + (activeIndex == link.index
            ? 'active' : '')"
            :key="link.id">
            <a :href="'#' + link.id">{{ link.title }}</a>
          </li>
        </ul>
      </div>
    </div>
  </v-layout>
</template>
<script>
import * as VBreadcrumbs from 'vuetify/es5/components/VBreadcrumbs'
import * as VGrid from 'vuetify/es5/components/VGrid'

import { setMeta } from '@/metaUtil'
import { pages, helpMap } from '@/docs/help.val'

const childElements = (createElement, children) => {
  if (!children || children.length === 0) return
  return createElement(
    'ul',
    {
      class: {
        childList: true,
      },
    },
    children.map(c => createElement('li',
      [
        createElement('router-link', { props: { to: c.url } }, [ c.title ]),
      ].concat(childElements(createElement, c.children)))
    )
  )
}

const ChildList = {
  functional: true,
  props:      ['children'],
  render:     function (createElement, context) {
    return childElements(createElement, context.props.children)
  },
}

export default {
  props: {
    base: {
      type:    String,
      default: '',
    },
    page: {
      type:    String,
      default: '',
    },
    scrollStyle: {
      type:    String,
      default: '',
    },
    currentOffset: {
      type:    Number,
      default: 0,
    },
  },
  data () {
    return {
      loading:  true,
      isBooted: false,
      doc:      '',
      timeout:  null,
    }
  },
  methods: {
    mount () {
      if (this.docPath) {
        this.isBooted = false
        import('../docs/' + this.docPath + '.md').then(r => {
          this.doc = r.default
          this.doc = this.doc || `<h2 class="display-2 mb-3">${this.meta.title}</h2>`
          this.loading = false
          setMeta({ title: `DartCannon - ${this.meta.title}` })
          this.timeout = setTimeout(() => {
            this.isBooted = true
          }, 200)
        }).catch(() => {
          this.doc = `<h2 class="display-2 mb-3">${this.meta.title}</h2>`
          this.error = true
          this.loading = false
        })
      } else {
        this.doc = `<h2 class="display-2">${this.meta.title}</h2>`
        this.loading = false
      }
    },
  },
  computed: {
    meta () {
      if (helpMap.hasOwnProperty(this.page)) {
        return helpMap[this.page]
      } else if (this.page === '') {
        return {
          title:    'DartCannon Help',
          url:      this.base,
          crumbs:   [],
          children: pages,
        }
      } else {
        return {
          title:    'No Such Page',
          url:      this.base + '/' + this.page,
          crumbs:   [],
          children: pages,
        }
      }
    },
    children () {
      if (this.page === '') return pages
      if (this.meta.hasOwnProperty('children')) return this.meta.children
      return []
    },
    docPath () {
      return this.meta.file
    },
    title () {
      return this.meta.title
    },
    crumbs () {
      let crumbs = [
        {
          text:  'Help',
          to:    '/help',
          exact: true,
        },
      ]
      if (this.meta.crumbs.length === 1) {
        let c = this.meta.crumbs[0]
        crumbs.push({
          text:  helpMap[c].title,
          to:    helpMap[c].url,
          exact: true,
        })
      }
      crumbs.push({
        text:  this.meta.title,
        to:    this.base + '/' + this.page,
        exact: true,
      })

      return crumbs
    },
    nav () {
      let nav = []
      // We need the html to be mounted in order to generate
      if (!this.isBooted) return nav

      let re = /<h(\d).*?id=['"](.*?)['"].*?>([^<]*)/gi
      let reMatch = []
      while ((reMatch = re.exec(this.doc)) !== null) {
        if (reMatch.length === 4) {
          nav.push({
            level: parseInt(reMatch[1]),
            id:    reMatch[2],
            title: reMatch[3].trim(),
          })
        }
      }
      let minLevel = nav.reduce((min, n) => { return Math.min(min, n.level) }, 99)
      let maxLevel = nav.reduce((max, n) => { return Math.max(max, n.level) }, 0)

      nav = nav.map(n => {
        n.level = n.level - minLevel
        return n
      })

      if (minLevel !== maxLevel) {
        nav = nav.filter(n => {
          return n.level === 1 || n.level === 2
        })
      }

      nav = nav.map((n, i) => {
        n.index = i
        const target = n.target || document.getElementById(n.id)

        if (target) {
          n.offsetTop = target.offsetTop
          n.target = target
        }
        return n
      })

      return nav
    },
    activeIndex () {
      const list = this.nav.slice().reverse()
      const index = list.findIndex(item => item.offsetTop - 100 < this.currentOffset)
      return index > -1
        ? list.length - 1 - index
        : 0
    },
  },
  mounted () {
    this.mount()
  },
  watch: {
    'page': function (to, from) {
      this.mount()
    },
  },
  components: {
    ChildList,
    ...VBreadcrumbs,
    ...VGrid,
  },
}
</script>
<style>
  .md--viewer .sidebar {
    flex: 0 1;
    min-width: 280px;
  }
  .md--text li {
    list-style-position: inside;
  }
  .md--text ul,
  .md--text ol {
    margin-bottom: 16px;
    padding-left: 4px;
  }

  .md--viewer .sidebar {
    padding-top: 32px;
  }
  .md--viewer .sidebar ul {
    padding-left: 0px;
  }
  .md--viewer .sidebar li {
    list-style-type: none;
  }
  .md--viewer .sidebar li a {
    padding-left: 18px;
    text-decoration: none;
    border-left: 2px solid transparent;
    transition: color .1s ease-in;
    color: #757575;
  }
  .md--viewer .sidebar li.active a {
    color: #01579b;
  }
  .md--viewer .sidebar li.active {
    border-left: 2px solid rgb(24, 103, 192);
  }

  .md--content {
    max-width: 800px;
  }

  .md--content img {
    max-width: 100%;
  }

  .childList {
    margin-left: 20px;
  }
</style>
